import type {
    AttributeType,
    CategoryListItem,
    ProductUnit,
} from '@/api/models';

/**
 * The catalog-import template's file format: its sheets, columns and
 * dropdown values, in both languages.
 *
 * Pure data and helpers, with no exceljs, i18next or React: the parse module
 * (#182) imports this file inside a Web Worker to find the Products sheet and
 * match headers in either language. The template's words are a file-format
 * contract, not UI copy. A file downloaded in Georgian last week must still
 * parse after an English copy edit, so they live here, not in the locale
 * files.
 */

export type TemplateLanguage = 'en' | 'ka';

/** One text per language: a header, a sheet name, a dropdown value. */
export type Bilingual = Readonly<Record<TemplateLanguage, string>>;

export const TEMPLATE_LANGUAGES: readonly TemplateLanguage[] = ['en', 'ka'];

/**
 * An i18next language (`ka-GE`, `en`) as a template language. Anything that
 * is not Georgian downloads the English template.
 */
export const resolveTemplateLanguage = (raw: string): TemplateLanguage =>
    raw.slice(0, 2).toLowerCase() === 'ka' ? 'ka' : 'en';

const TEMPLATE_FILENAMES: Bilingual = {
    en: 'BOS_catalog_template.xlsx',
    ka: 'BOS_კატალოგის_შაბლონი.xlsx',
};

export const templateFilename = (language: TemplateLanguage): string =>
    TEMPLATE_FILENAMES[language];

/** Body rows that carry the dropdowns; one import takes at most 2,000 lines. */
export const TEMPLATE_BODY_ROWS = 2000;

/** Row 1 is the header row; the body is rows 2 to 2,001. */
export const TEMPLATE_FIRST_BODY_ROW = 2;
export const TEMPLATE_LAST_BODY_ROW = TEMPLATE_BODY_ROWS + 1;

// ---- Sheets -----------------------------------------------------------------

export type TemplateSheetKey =
    'products' | 'categories' | 'brands' | 'suppliers';

export const TEMPLATE_SHEET_NAMES: Readonly<
    Record<TemplateSheetKey, Bilingual>
> = {
    products: { en: 'Products', ka: 'პროდუქტები' },
    categories: { en: 'Categories', ka: 'კატეგორიები' },
    brands: { en: 'Brands', ka: 'ბრენდები' },
    suppliers: { en: 'Suppliers', ka: 'მომწოდებლები' },
};

/** The Categories sheet's three headers. */
export const CATEGORY_SHEET_HEADERS: Readonly<
    Record<'name' | 'parent' | 'hint', Bilingual>
> = {
    name: { en: 'Name', ka: 'დასახელება' },
    parent: { en: 'Parent', ka: 'მშობელი კატეგორია' },
    hint: { en: 'Hint', ka: 'მინიშნება' },
};

const AXES_HINT_PREFIX: Bilingual = {
    en: 'variation axes',
    ka: 'ვარიაციის ღერძები',
};

const REQUIRED_MARK: Bilingual = {
    en: 'required',
    ka: 'სავალდებულო',
};

// ---- Products columns -------------------------------------------------------

/**
 * The fixed columns, keyed by the import line's field name (bos-api
 * `CatalogImportLineRequest`), so the parser maps a column straight to its
 * field.
 */
export type TemplateFieldKey =
    | 'barcode'
    | 'sku'
    | 'name'
    | 'category'
    | 'brand'
    | 'supplier'
    | 'unit'
    | 'vatType'
    | 'lotNumber'
    | 'quantity'
    | 'cost'
    | 'salePrice'
    | 'wholesalePrice'
    | 'minimumQuantity'
    | 'purchaseDate'
    | 'expiryDate'
    | 'saleDueDate';

/** How a column's cells are formatted. */
export type TemplateCellFormat = 'text' | 'number' | 'amount' | 'date';

export type TemplateFieldColumn = {
    kind: 'field';
    key: TemplateFieldKey;
    header: Bilingual;
    format: TemplateCellFormat;
    width: number;
};

const field = (
    key: TemplateFieldKey,
    header: Bilingual,
    format: TemplateCellFormat,
    width: number,
): TemplateFieldColumn => ({ kind: 'field', key, header, format, width });

/**
 * Before the attribute columns. Barcode, SKU and lot number are text-formatted
 * so Excel keeps a 13-digit barcode or a 15-digit IMEI as typed instead of
 * `6.95316E+12`.
 */
export const COLUMNS_BEFORE_ATTRIBUTES: readonly TemplateFieldColumn[] = [
    field('barcode', { en: 'Barcode', ka: 'შტრიხკოდი' }, 'text', 18),
    field('sku', { en: 'SKU', ka: 'SKU' }, 'text', 16),
    field('name', { en: 'Name', ka: 'დასახელება' }, 'text', 32),
    field('category', { en: 'Category', ka: 'კატეგორია' }, 'text', 22),
    field('brand', { en: 'Brand', ka: 'ბრენდი' }, 'text', 18),
    field('supplier', { en: 'Supplier', ka: 'მომწოდებელი' }, 'text', 22),
    field('unit', { en: 'Unit', ka: 'ერთეული' }, 'text', 12),
    field('vatType', { en: 'VAT', ka: 'დღგ' }, 'text', 14),
];

/** After the attribute columns. */
export const COLUMNS_AFTER_ATTRIBUTES: readonly TemplateFieldColumn[] = [
    field('lotNumber', { en: 'Lot number', ka: 'პარტიის ნომერი' }, 'text', 20),
    field('quantity', { en: 'Quantity', ka: 'რაოდენობა' }, 'number', 12),
    field('cost', { en: 'Cost', ka: 'თვითღირებულება' }, 'amount', 16),
    field('salePrice', { en: 'Sale price', ka: 'გასაყიდი ფასი' }, 'amount', 14),
    field(
        'wholesalePrice',
        { en: 'Wholesale price', ka: 'საბითუმო ფასი' },
        'amount',
        16,
    ),
    field(
        'minimumQuantity',
        { en: 'Minimum quantity', ka: 'მინიმალური რაოდენობა' },
        'number',
        18,
    ),
    field(
        'purchaseDate',
        { en: 'Purchase date', ka: 'შესყიდვის თარიღი' },
        'date',
        16,
    ),
    field(
        'expiryDate',
        { en: 'Expiry date', ka: 'ვარგისიანობის ვადა' },
        'date',
        18,
    ),
    field(
        'saleDueDate',
        { en: 'Sale due date', ka: 'გაყიდვის ბოლო ვადა' },
        'date',
        18,
    ),
];

/** Every fixed column, in template order (the attribute columns sit between). */
export const TEMPLATE_FIELD_COLUMNS: readonly TemplateFieldColumn[] = [
    ...COLUMNS_BEFORE_ATTRIBUTES,
    ...COLUMNS_AFTER_ATTRIBUTES,
];

/** A file without these headers is not this template. */
export const REQUIRED_FIELD_KEYS: readonly TemplateFieldKey[] = [
    'sku',
    'name',
    'quantity',
    'salePrice',
];

/** One column per attribute type that is a Variation axis somewhere, headed by its name. */
export type TemplateAttributeColumn = {
    kind: 'attribute';
    attributeTypeId: string;
    /** The type's name as stored, in both languages; the parser matches it case-insensitively. */
    header: string;
    width: number;
};

export type TemplateColumn = TemplateFieldColumn | TemplateAttributeColumn;

// ---- Dropdown values ----------------------------------------------------------

/**
 * The two VAT values the template offers, with the wire value each one means
 * (`Reduced` is not offered). Standard: the cost includes VAT; Exempt: the
 * cost is the cost.
 */
export const TEMPLATE_VAT_VALUES: Readonly<
    Record<'standard' | 'exempt', Bilingual>
> = {
    standard: { en: 'Standard', ka: 'სტანდარტული' },
    exempt: { en: 'Exempt', ka: 'ნულოვანი' },
};

/**
 * Units are offered by their wire names in both languages: the server
 * matches a unit by its `ProductUnit` name, so a translated label would need
 * mapping on read.
 */
export const TEMPLATE_UNITS: readonly ProductUnit[] = [
    'Piece',
    'Kilogram',
    'Gram',
    'Liter',
    'Milliliter',
    'Meter',
    'Pack',
];

// ---- Inputs and their pure shaping ------------------------------------------

/** What the builder needs of a category: the picker's items carry it. */
export type TemplateCategory = Pick<
    CategoryListItem,
    'id' | 'name' | 'parentId'
>;

/** What the builder needs of an attribute type (one `GET attribute-types`). */
export type TemplateAttributeType = Pick<
    AttributeType,
    'id' | 'name' | 'values' | 'variationCategories'
>;

/** Attribute types that get a Products column: a Variation axis of at least one category. */
export const attributeColumnTypes = (
    attributeTypes: readonly TemplateAttributeType[],
): TemplateAttributeType[] =>
    attributeTypes.filter(
        (type) => (type.variationCategories ?? []).length > 0,
    );

/** A type's Active values in their dictionary order: its dropdown. */
export const activeValueNames = (type: TemplateAttributeType): string[] =>
    type.values
        .filter((value) => value.status === 'Active')
        .sort((a, b) => a.position - b.position)
        .map((value) => value.name);

/** The Products sheet's columns, in order, for a set of attribute types. */
export const templateColumns = (
    attributeTypes: readonly TemplateAttributeType[],
): TemplateColumn[] => [
    ...COLUMNS_BEFORE_ATTRIBUTES,
    ...attributeColumnTypes(attributeTypes).map(
        (type): TemplateAttributeColumn => ({
            kind: 'attribute',
            attributeTypeId: type.id,
            header: type.name,
            width: Math.min(Math.max(type.name.length + 4, 14), 28),
        }),
    ),
    ...COLUMNS_AFTER_ATTRIBUTES,
];

/** A column's header in one language. */
export const columnHeader = (
    column: TemplateColumn,
    language: TemplateLanguage,
): string =>
    column.kind === 'field' ? column.header[language] : column.header;

/**
 * The Categories sheet's hint for one category: its Variation axes, required
 * ones marked, e.g. `variation axes: Storage Capacity (required), Color`.
 * Empty when the category has none.
 */
export const categoryAxesHint = (
    categoryId: string,
    attributeTypes: readonly TemplateAttributeType[],
    language: TemplateLanguage,
): string => {
    const axes = attributeTypes.flatMap((type) => {
        const link = (type.variationCategories ?? []).find(
            (category) => category.categoryId === categoryId,
        );
        if (link === undefined) {
            return [];
        }
        return [
            link.isRequired
                ? `${type.name} (${REQUIRED_MARK[language]})`
                : type.name,
        ];
    });
    return axes.length === 0
        ? ''
        : `${AXES_HINT_PREFIX[language]}: ${axes.join(', ')}`;
};

/** Excel refuses these in a sheet name; 31 characters is its limit. */
const SHEET_NAME_FORBIDDEN = /[\\/?*[\]:]/g;
const SHEET_NAME_MAX = 31;

/**
 * A sheet name Excel accepts and no other sheet has (Excel compares sheet
 * names ignoring case): forbidden characters become spaces (runs of spaces
 * become one), the name is cut
 * to 31 characters, and a clash gets ` (2)`, ` (3)`… within the limit.
 */
export const uniqueSheetName = (
    wanted: string,
    taken: ReadonlySet<string>,
): string => {
    const cleaned =
        wanted
            .replace(SHEET_NAME_FORBIDDEN, ' ')
            .replace(/^'+|'+$/g, '')
            .replace(/\s+/g, ' ')
            .trim() || 'Sheet';
    const isTaken = (name: string) => taken.has(name.toLowerCase());
    const base = cleaned.slice(0, SHEET_NAME_MAX).trim();
    if (!isTaken(base)) {
        return base;
    }
    for (let n = 2; ; n += 1) {
        const suffix = ` (${n})`;
        const candidate = `${base.slice(0, SHEET_NAME_MAX - suffix.length).trim()}${suffix}`;
        if (!isTaken(candidate)) {
            return candidate;
        }
    }
};
